import { navigate } from '@reach/router';
import { getFetcher, getFetcherWithResponse, postFetcherWithResponse } from '@startlibs/utils';
import React, { createContext, useState, useContext, useEffect } from 'react';
import { getRefreshToken, setRefreshToken } from '../hooks/useRefresh';
import { EXPIRED_TOKEN_TRY_REFRESH } from '../pages/LoginPage';
import { setJwt } from '../hooks/useJwt';
import { MENU_ITEMS } from '../utils/constants';
import { setNotification } from '../components/Notifications';

// Create the context
const AuthContext = createContext();

let isProdEnv = process.env.NODE_ENV !== 'development'

export const fetchWithTokenRefresh = (fetchFunction, url, data, options, { jwt, setJwt, setRefreshToken }, redirectUrl = 'login', withNotification = false) => {
  const doFetch = (token) => fetchFunction(url, data, { ...options, headers: { 'Authorization': `Bearer ${token}`, ...options.headers } });

  return doFetch(jwt).catch(async err => {
    if (err && err.length > 1) {
      const status = err[1]?.status || 0;
      const messageCode = err[0]?.message;
      // if (status === 403 && messageCode === EXPIRED_TOKEN_TRY_REFRESH) {
      if (status === 401) {
        // Token expired, refresh token
        return postFetcherWithResponse('/gw/refresh', {}, {
          headers: {
            'Authorization': 'Bearer ' + await getRefreshToken(),
            'X-Auth-Token': jwt,
          }
        }).then(([data, res]) => {
          const authToken = res.headers.get('X-Auth-Token');
          setJwt(authToken, 'worklist');
          const refreshToken = res.headers.get('X-Refresh-Token');
          setRefreshToken(refreshToken, 'refreshToken');
          // Retry the original request with new token
          return doFetch(authToken);
        }).catch(refreshErr => {
          const refreshStatus = refreshErr[1]?.status || 0;
          if (refreshStatus === 401) {
            if (withNotification) {
              // setNotification({ type: 'alert', msg: SESSION_EXPIRED_MSG });
              navigate(`${redirectUrl}#session-expired`);
              return  
            }
            navigate(redirectUrl);
            // navigate('/');
          }
          throw refreshErr;
        });
      }
    }
    throw err;
  });
}

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null); // Set initial state to null or an object with user data
  const [menu, setMenu] = useState([]);
  const [instanceData, setInstanceData] = useState()
  const [loadedData, setLoadedData] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [pacsDbName, setPacsDbName] = useState('')


  const urlPath = window.location.pathname

  // Helper function to get URL parameters
  const getURLParam = (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };


  // Retrieve tokens from URL params or localStorage
  const jwtFromURL = (urlPath === '/instantshare' || urlPath === '/instantshare.php' || urlPath === '//instantshare' || urlPath === '//instantshare.php') ? false : getURLParam('jwt');
  // Do not set jwt token when it is a share link
  
  const refreshTokenFromURL = getURLParam('refreshToken') || getURLParam('refresh');

  const jwt = jwtFromURL || window.localStorage.getItem('worklist-jwt')
  if (jwt) {
    setJwt(jwt, 'worklist')
  }
  const refreshToken = refreshTokenFromURL || window.localStorage.getItem('refreshToken')
  if (refreshToken) {
    setRefreshToken(refreshToken, 'refreshToken')
  } else {
    setRefreshToken('')
  }

  // console.log('jwt: ' + jwt)
  // console.log('refreshToken: ' + refreshToken)

  // First of all, get the instance data
  useEffect(() => {
    try {
      getFetcher('/gw/customer-info')
        .then(res => {

          if (res && res.customerInfo) {
            const data = res.customerInfo
            if (data) {
              let base64decoded = atob(data)
              let json = JSON.parse(base64decoded)
              setInstanceData(json)
              window.localStorage.setItem("instanceData", JSON.stringify(json))
              setLoadedData(true)
            }
          }
        })
        .catch(err => {
          console.log(err)
        })

    } catch (e) {
      console.log(e)
      setLoadedData(true)
    }
  }, [])

  useEffect(() => {
    try {
      setIsCheckingAuth(true)
      const path = window.location.pathname
      if (path !== '/login' && path !== '/spa/login' && path !== "/pas-login") {

        // check public routes
        if (path === '/instantshare' || path === '/instantshare.php' 
          || path === '//instantshare' || path === '//instantshare.php'
          || path === '/vivareports_secureshared' || path === '/vivareports_secureshared.php' 
          || path === '//vivareports_secureshared' || path === '//vivareports_secureshared.php'
        ) {
          if (path === '/instantshare.php') {
            // console.log('instantshare.php')
          }
          return
        }
        if(path === '/dicomuploader' || path === '/dicomuploader.php'){
          return
        }

        if((path === '/user-management' || path === '/activity-logs' || path === '/analytics') && jwt){
          return
        }

        if (jwt) {
          postFetcherWithResponse('/gw/refresh', {}, {
            headers: {
              // 'Authorization': 'Bearer ' + getRefreshToken(), // useRefresh from URL
              'Authorization': 'Bearer ' + refreshToken,
              'X-Auth-Token': jwt,
            }
          }).then(([data, res]) => {
              const authToken = res.headers.get('X-Auth-Token');
              setJwt(authToken, 'worklist');
              const refreshToken = res.headers.get('X-Refresh-Token');
              setRefreshToken(refreshToken, 'refreshToken');
              fetchWithTokenRefresh(
                  getFetcherWithResponse,
                  '/gw/worklist/options-and-rights',
                  {},
                  {},
                  { jwt: authToken, setJwt: (jwt) => setJwt(jwt, 'worklist'), setRefreshToken: (refreshToken) => setRefreshToken(refreshToken, 'refreshToken') },
                  'login',
                  false
              ).then(([data, res]) => {
                // Filter the menus based on the presence of the name in data.auth array
                const availableMenuItens = MENU_ITEMS.filter(item => data.menus.includes(item.name));
                setMenu(availableMenuItens)
                setAuthUser(data?.username)
                setPacsDbName(data?.pacsdbname)
                // window.location.href = '/'
                // navigate('/')
                // if (path === '/pas-login' || path === '/spa/pas-login') {
                //   // navigate('/')
                //   // // remove the / from the URL
                //   setTimeout(() => {
                //     window.history.replaceState({}, document.title, window.location.pathname);  
                //   }, 2500)
                // }
                // setIsCheckingAuth(false)
                return
              })
                .catch(err => {
                  console.log(err)
                  if (err && err.length > 1) {
                    const status = err[1]?.status || 0
                    if (status === 403) {
                      setNotification({ type: 'alert', msg: 'You do not have permission to access this page.'})
                      return
                    }
                    navigate('/login')
                  }
                })
                .finally(() => {
                  setIsCheckingAuth(false)
                })
          }).catch(([res, refreshErr]) => {
            // console.log(res, refreshErr)
            const refreshStatus = refreshErr?.status || 0;
            if (refreshStatus === 401) {
              localStorage.removeItem('worklist-jwt')
              localStorage.removeItem('refreshToken')
              navigate('login#session-expired')
            }
            throw refreshErr;
          })

        } else {

          // redirect to login
          navigate('/login')
        }
      }
      // setIsCheckingAuth(false)
    } catch (e) {
      console.log(e)
    }
  }, [])

  return (
    <AuthContext.Provider value={{
      authUser, setAuthUser,
      menu, setMenu,
      instanceData, setInstanceData,
      loadedData, setLoadedData,
      pacsDbName, setPacsDbName,
      isCheckingAuth, setIsCheckingAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a custom hook to easily use the context
export const useAuth = () => useContext(AuthContext);
